/**
 * 霓虹蛇境 (Neon Snake Realm) - 程序化音效模块
 *
 * 本模块使用 Web Audio API 生成所有游戏音效，无需外部音频文件。
 * 所有音效均为纯程序化合成，体积为零，加载时间为零。
 *
 * 模块职责：
 * - 管理 AudioContext 单例（懒初始化，处理浏览器自动播放策略）
 * - 提供吃食物、收集道具、游戏结束、移动、启动等音效函数
 * - 所有函数在 AudioContext 不可用时优雅降级（静默失败）
 *
 * 浏览器兼容性：
 * - 现代浏览器均支持 Web Audio API
 * - SSR 环境下 AudioContext 不存在，所有函数安全返回
 * - 浏览器自动播放策略：AudioContext 在首次用户交互后自动恢复
 */

// ============================================================================
// 模块级单例
// ============================================================================

/** AudioContext 单例实例，懒初始化 */
let audioContext: AudioContext | null = null;

// ============================================================================
// AudioContext 管理
// ============================================================================

/**
 * 懒初始化并返回 AudioContext 单例。
 *
 * 处理以下场景：
 * - 浏览器不支持 Web Audio API → 返回 null
 * - SSR 环境（无 window）→ 返回 null
 * - AudioContext 被浏览器暂停（自动播放策略）→ 调用 resume()
 *
 * @returns AudioContext 实例，或 null（不可用时）
 */
export function createAudioContext(): AudioContext | null {
  // SSR 环境或无 window 对象
  if (typeof window === 'undefined') {
    return null;
  }

  // 浏览器不支持 Web Audio API
  const AudioContextClass =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }

  // 懒初始化单例
  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  // 处理浏览器自动播放策略：若 AudioContext 处于 suspended 状态，尝试恢复
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {
      // 静默失败：用户尚未与页面交互，音效将在交互后自动恢复
    });
  }

  return audioContext;
}

// ============================================================================
// 内部工具函数
// ============================================================================

/**
 * 创建 GainNode 并设置音量包络。
 *
 * @param ctx - AudioContext 实例
 * @param startTime - 开始时间（相对于 AudioContext.currentTime）
 * @param maxGain - 最大增益值 (0~1)
 * @param attackTime - 起音时间（秒）
 * @param decayTime - 衰减时间（秒）
 * @param sustainLevel - 保持电平 (0~1)，衰减后维持的值
 * @param releaseTime - 释音时间（秒），从 sustainLevel 衰减到 0
 * @returns GainNode 实例
 */
function createGainEnvelope(
  ctx: AudioContext,
  startTime: number,
  maxGain: number,
  attackTime: number,
  decayTime: number,
  sustainLevel: number = 0,
  releaseTime: number = 0
): GainNode {
  const gainNode = ctx.createGain();
  const now = startTime;

  // 初始静音
  gainNode.gain.setValueAtTime(0, now);
  // 起音：从 0 线性上升到 maxGain
  gainNode.gain.linearRampToValueAtTime(maxGain, now + attackTime);
  // 衰减：从 maxGain 线性下降到 sustainLevel
  gainNode.gain.linearRampToValueAtTime(sustainLevel, now + attackTime + decayTime);

  // 释音（如果指定）
  if (releaseTime > 0) {
    gainNode.gain.linearRampToValueAtTime(0, now + attackTime + decayTime + releaseTime);
  }

  return gainNode;
}

/**
 * 安全停止并断开 oscillator 节点。
 * 在音效播放完毕后调用，防止内存泄漏。
 *
 * @param osc - OscillatorNode 实例
 * @param stopTime - 停止时间（相对于 AudioContext.currentTime）
 */
function scheduleOscillatorStop(osc: OscillatorNode, stopTime: number): void {
  try {
    osc.stop(stopTime);
    osc.onended = () => {
      osc.disconnect();
    };
  } catch {
    // 如果 oscillator 已经停止，忽略错误
  }
}

// ============================================================================
// 音效函数
// ============================================================================

/**
 * 播放吃食物音效。
 *
 * 音效特征：
 * - 短促上升的"啵"声
 * - 正弦波，频率从 400Hz 扫到 800Hz
 * - 持续 0.1 秒
 * - 音量包络：起音 0.01s → 衰减到 0（0.1s）
 */
export function playEatSound(): void {
  const ctx = createAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const duration = 0.1;

  // 创建 oscillator
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(400, now);
  osc.frequency.linearRampToValueAtTime(800, now + duration);

  // 创建音量包络
  const gainNode = createGainEnvelope(ctx, now, 0.3, 0.01, duration, 0, 0);

  // 连接节点
  osc.connect(gainNode);
  gainNode.connect(ctx.destination);

  // 启动并调度停止
  osc.start(now);
  scheduleOscillatorStop(osc, now + duration + 0.01);
}

/**
 * 播放收集道具音效。
 *
 * 音效特征：
 * - 魔法般的"叮咚"声
 * - 两个正弦波 oscillator：600Hz 和 900Hz
 * - 第二个 oscillator 延迟 0.05s 启动
 * - 每个持续 0.15s
 * - 音量包络：起音 0.01s → 衰减到 0（0.15s）
 */
export function playPowerUpSound(): void {
  const ctx = createAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const duration = 0.15;
  const stagger = 0.05;

  // 第一个音：600Hz
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(600, now);

  const gain1 = createGainEnvelope(ctx, now, 0.25, 0.01, duration, 0, 0);
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.start(now);
  scheduleOscillatorStop(osc1, now + duration + 0.01);

  // 第二个音：900Hz，延迟 stagger 秒
  const osc2Start = now + stagger;
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(900, osc2Start);

  const gain2 = createGainEnvelope(ctx, osc2Start, 0.2, 0.01, duration, 0, 0);
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.start(osc2Start);
  scheduleOscillatorStop(osc2, osc2Start + duration + 0.01);
}

/**
 * 播放游戏结束音效。
 *
 * 音效特征：
 * - 下降的"失败"音
 * - 锯齿波，频率从 500Hz 扫到 100Hz
 * - 持续 0.5s
 * - 音量包络：起音 0.02s → 衰减到 0（0.5s）
 */
export function playGameOverSound(): void {
  const ctx = createAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const duration = 0.5;

  // 创建 oscillator（锯齿波）
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(500, now);
  osc.frequency.linearRampToValueAtTime(100, now + duration);

  // 创建音量包络
  const gainNode = createGainEnvelope(ctx, now, 0.2, 0.02, duration, 0, 0);

  // 连接节点
  osc.connect(gainNode);
  gainNode.connect(ctx.destination);

  // 启动并调度停止
  osc.start(now);
  scheduleOscillatorStop(osc, now + duration + 0.01);
}

/**
 * 播放蛇移动音效。
 *
 * 音效特征：
 * - 极微弱的"滴答"声
 * - 正弦波，200Hz
 * - 持续 0.03s
 * - 最大增益仅 0.05（几乎听不见，仅提供触觉反馈感）
 */
export function playMoveSound(): void {
  const ctx = createAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const duration = 0.03;

  // 创建 oscillator
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(200, now);

  // 创建音量包络（极低音量）
  const gainNode = createGainEnvelope(ctx, now, 0.05, 0.005, duration, 0, 0);

  // 连接节点
  osc.connect(gainNode);
  gainNode.connect(ctx.destination);

  // 启动并调度停止
  osc.start(now);
  scheduleOscillatorStop(osc, now + duration + 0.01);
}

/**
 * 播放游戏启动音效。
 *
 * 音效特征：
 * - 短促的启动"叮铃"声
 * - 三个上升正弦音：300Hz → 500Hz → 800Hz
 * - 每个音持续 0.08s，依次播放
 * - 音量包络：起音 0.005s → 衰减到 0（0.08s）
 */
export function playStartSound(): void {
  const ctx = createAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const noteDuration = 0.08;
  const frequencies = [300, 500, 800];

  frequencies.forEach((freq, index) => {
    const startTime = now + index * noteDuration;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startTime);

    const gainNode = createGainEnvelope(ctx, startTime, 0.2, 0.005, noteDuration, 0, 0);

    osc.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.start(startTime);
    scheduleOscillatorStop(osc, startTime + noteDuration + 0.01);
  });
}